/**
 * Scraper settings component
 * Configure scraping behavior
 */

import { useEffect } from 'react';
import { Card, Form, InputNumber, Switch, Button, Space, Typography, Tooltip, Divider, Alert } from 'antd';
import { InfoCircleOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ScraperSettings as ScraperSettingsType } from '@/types/api';

const { Text } = Typography;

interface ScraperSettingsProps {
  settings?: ScraperSettingsType;
  loading?: boolean;
  onSave?: (settings: ScraperSettingsType) => void;
  onReset?: () => void;
}

const DEFAULT_SETTINGS: ScraperSettingsType = {
  maxWorkers: 1,
  concurrency: 1,
  maxBrowsers: 1,
  extractionConcurrency: 1,
  pageTimeout: 30000,
  navigationTimeout: 60000,
  waitTimeout: 10000,
  headless: true,
};

export function ScraperSettings({
  settings,
  loading,
  onSave,
  onReset,
}: ScraperSettingsProps) {
  const [form] = Form.useForm();

  // Update form values when settings are loaded from API
  useEffect(() => {
    if (settings) {
      form.setFieldsValue(settings);
    }
  }, [settings, form]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      onSave?.(values);
    });
  };

  const handleReset = () => {
    form.setFieldsValue(DEFAULT_SETTINGS);
    onReset?.();
  };

  return (
    <Card
      title="Scraper Settings"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset Defaults
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={loading}
          >
            Save Settings
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={DEFAULT_SETTINGS}
        disabled={loading}
      >
        {/* Concurrency settings */}
        <Typography.Title level={5}>Concurrency</Typography.Title>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Form.Item
            name="concurrency"
            label={
              <Space>
                Cell Concurrency
                <Tooltip title="Number of grid cells to process in parallel. Each cell uses one browser tab.">
                  <InfoCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                </Tooltip>
              </Space>
            }
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="extractionConcurrency"
            label={
              <Space>
                Extraction Tabs
                <Tooltip title="Parallel tabs for place detail extraction. Only works when Max Browsers = 1. With Max Browsers > 1, extraction is sequential per cell.">
                  <InfoCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                </Tooltip>
              </Space>
            }
          >
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="maxBrowsers"
            label={
              <Space>
                Max Browsers
                <Tooltip title="Browser instances. Set to 1 for fast parallel extraction within cells. Set higher to process multiple cells simultaneously (but extraction becomes sequential per cell).">
                  <InfoCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                </Tooltip>
              </Space>
            }
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <Divider />

        {/* Timeout settings */}
        <Typography.Title level={5}>Timeouts (ms)</Typography.Title>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Form.Item name="pageTimeout" label="Page Load Timeout">
            <InputNumber min={5000} max={120000} step={1000} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="navigationTimeout" label="Navigation Timeout">
            <InputNumber min={5000} max={120000} step={1000} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="waitTimeout" label="Element Wait Timeout">
            <InputNumber min={1000} max={60000} step={1000} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <Divider />

        {/* Browser settings */}
        <Typography.Title level={5}>Browser</Typography.Title>
        <Form.Item
          name="headless"
          label="Headless Mode"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Headless"
            unCheckedChildren="Visible"
          />
        </Form.Item>

        <Alert
          type="info"
          message="Settings synchronization"
          description="Settings are saved to Valkey and workers poll for changes every 30 seconds. Timeout and delay settings apply immediately. Concurrency and browser count changes require worker restart to fully take effect."
          style={{ marginTop: 16 }}
        />
      </Form>
    </Card>
  );
}

export default ScraperSettings;
